import React, { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import {
  UPLOAD_REVIEW_REQUESTS_API,
  UploadReviewRequestsPayload,
} from 'constants/payloads/uploadReviewRequests';
import { useApiGateway } from 'hooks/useApi';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import Minus from 'svg/Minus';
import PlusPlain from 'svg/PlusPlain';
import { UploadReviewsFormData } from 'screens/CoachLoggedInScreen/props';
import { uploadReviewsSchema } from 'screens/CoachLoggedInScreen/utils';
import { Button, ButtonText } from 'components/Button';
import InputField from 'components/coaches-builder/InputField';
import { InputType } from 'components/coaches-builder/InputField/InputField';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';

interface UploadReviewsModalProps {
  isOpen: boolean;
  handleClose: () => void;
  onSuccess?: () => void;
}

const UploadReviewsModal: React.FC<UploadReviewsModalProps> = ({
  isOpen,
  handleClose,
  onSuccess,
}) => {
  const getNestedFieldErrors = (errors: any) => {
    const nestedErrors: any = {};
    Object.keys(errors).forEach((key) => {
      if (key === 'links' && Array.isArray(errors[key])) {
        errors[key].forEach((linkError: any, index: number) => {
          if (linkError?.url) {
            nestedErrors[`links.${index}.url`] = linkError.url;
          }
        });
      }
    });

    return nestedErrors;
  };
  const [step, setStep] = useState<'input' | 'success'>('input');
  const viewer = useViewer();

  const { post, isLoading } = useApiGateway<UploadReviewRequestsPayload>(
    UPLOAD_REVIEW_REQUESTS_API,
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<UploadReviewsFormData>({
    resolver: zodResolver(uploadReviewsSchema),
    defaultValues: {
      links: [{ url: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'links',
  });

  const onSubmit = async (data: UploadReviewsFormData) => {
    try {
      await post({
        payload: { urls: data.links.map((link) => link.url.trim()) },
      });

      setStep('success');
      // Call success callback if provided
      onSuccess?.();
    } catch (error) {
      toast.error('Error submitting review requests');
      console.error('Error submitting review requests:', error);
    }
  };

  const handleDone = () => {
    handleClose();
    setTimeout(() => {
      setStep('input');
      reset();
    }, 300);
  };

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="sm:max-w-2xl">
      <div className="flex h-full flex-col">
        {/* Modal Header */}
        <div
          className={classNames(
            'flex flex-row items-center justify-between border-b border-color-border-card-lightmode px-10 py-5 dark:border-color-border-card-darkmode max-sm:px-ds-lg',
            step === 'success' ? 'border-b-0' : '',
          )}
        >
          <h2 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile">
            {step === 'input' ? 'Upload your reviews' : ''}
          </h2>
          <ButtonText onClick={handleClose}>
            <CloseIcon className="h-6 w-6 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary max-sm:h-3.5 max-sm:w-3.5" />
          </ButtonText>
        </div>

        {/* Modal Content */}
        <div className="flex flex-1 flex-col items-center px-10 py-4 max-sm:px-ds-lg">
          {step === 'input' ? (
            <div className="flex w-full flex-col gap-8">
              {/* Description */}
              <div className="flex flex-row items-center justify-center py-6">
                <p className="typography-product-body-highlight max-w-[30.125rem] text-center text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  Share links to any profiles with reviews you'd like imported—we'll email you when
                  we've uploaded them to your profile.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col gap-6">
                <div className="flex flex-col gap-3">
                  <h3 className="typography-product-subheading text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    Enter links to your other profile pages with reviews
                  </h3>

                  {fields.map((field, index) => (
                    <div key={field.id} className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          <InputField
                            name={`links.${index}.url`}
                            register={register}
                            errors={getNestedFieldErrors(errors)}
                            placeholder="https://"
                            inputType={InputType.Regular}
                          />
                        </div>
                        {fields.length > 1 && (
                          <ButtonText type="button" onClick={() => remove(index)}>
                            <Minus className="h-4 w-4 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
                          </ButtonText>
                        )}
                      </div>
                    </div>
                  ))}

                  <ButtonText
                    type="button"
                    onClick={() => append({ url: '' })}
                    className="flex items-center gap-1 text-color-brand-primary"
                  >
                    <PlusPlain className="h-4 w-4" />
                    Add another Link
                  </ButtonText>
                </div>
              </form>
            </div>
          ) : (
            /* Success Step */
            <div className="flex flex-1 flex-col items-center justify-center gap-8">
              {/* Success Icon */}
              <div className="flex h-[7.5rem] w-[7.5rem] items-center justify-center">
                <img src="/images/players/check.png" alt="Success" className="h-full w-full" />
              </div>

              {/* Success Content */}
              <div className="flex max-w-[30rem] flex-col items-center gap-6 text-center max-sm:gap-3">
                <h2 className="typography-product-display text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  Thanks! We've Got It
                </h2>
                <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                  Your information has been submitted to the Bounce team. Keep an eye on your inbox
                  — we'll be in touch with updates shortly.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          className={classNames(
            'flex flex-row items-center justify-end px-10 py-5 max-sm:px-ds-lg',
            step === 'input' ? 'mt-[11.25rem]' : 'mt-16',
          )}
        >
          {step === 'input' ? (
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="primary"
              size="lg"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Help me import Reviews'}
            </Button>
          ) : (
            <Button onClick={handleDone} variant="primary" size="lg" className="w-full">
              Done
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default UploadReviewsModal;
